import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import PropTypes from 'prop-types'

const backdrop = {
  visible: { opacity: 1 },
  hidden: { opacity: 0, transition: { delay: 0.5 } },
}

const InternalOverlay = ({
  children,
  onClose,
  className,
  autoFocus,
  bgOpacityNone,
  preventEscBackdropClose = false,
}) => {
  const containerRef = useRef()

  useEffect(() => {
    const container = containerRef.current
    if (autoFocus && container) {
      container.focus()
    }
    document.body.classList.add('overflow-hidden')
    return () => {
      if (autoFocus && container) {
        container.blur()
      }
      document.body.classList.remove('overflow-hidden')
    }
  }, [autoFocus])

  const handleClose = e => {
    if (!preventEscBackdropClose && e.target === e.currentTarget) {
      onClose(e)
    }
  }

  const handleKeyboardClose = e => {
    if (!preventEscBackdropClose && e.key === 'Escape') {
      onClose(e)
    }
  }

  return createPortal(
    <motion.div
      ref={containerRef}
      onClick={handleClose}
      onKeyDown={handleKeyboardClose}
      className={classnames('fixed inset-0 overflow-y-auto bg-black  z-1000', className, {
        'bg-opacity-80': !bgOpacityNone,
        'bg-opacity-0': bgOpacityNone,
      })}
      tabIndex="-1"
      variants={backdrop}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      {children}
    </motion.div>,
    document.body
  )
}

const Overlay = ({ show, ...rest }) => (
  <AnimatePresence>{show && <InternalOverlay {...rest} />}</AnimatePresence>
)

Overlay.propTypes = {
  autoFocus: PropTypes.bool,
  preventEscBackdropClose: PropTypes.bool,
  bgOpacityNone: PropTypes.bool,
  className: PropTypes.string,
}

Overlay.defaultProps = {
  onClose: () => {},
  autoFocus: true,
}

export default Overlay
